import { useState, useEffect, useCallback, FormEvent } from 'react';
import { isCancel } from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { fetchLoginCaptcha, imageBase64ToDataUrl } from '@/api';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';
import CaptchaInput from './components/CaptchaInput';

type LoginFieldErrors = {
  email?: string;
  password?: string;
  captcha?: string;
};

/**
 * Captcha policy:
 * - `0` — captcha is required on every sign-in.
 * - `N > 0` — captcha is required only after `N` failed sign-ins; the amber notice below is shown only in this mode (not when `N === 0`).
 */
const LOGIN_FAIL_CAPTCHA_THRESHOLD = 0;

function computeFieldErrors(
  email: string,
  password: string,
  requireCaptcha: boolean,
  captchaReady: boolean,
  captchaValid: boolean
): LoginFieldErrors {
  const e: LoginFieldErrors = {};
  const em = email.trim();
  if (!em) e.email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Please enter a valid email address.';
  if (!password) e.password = 'Please enter your password.';
  else if (password.length < 6) e.password = 'Password must be at least 6 characters.';
  if (requireCaptcha) {
    if (!captchaReady) e.captcha = 'Captcha is not ready. Tap refresh or wait.';
    else if (!captchaValid) e.captcha = 'Please enter the correct captcha code.';
  }
  return e;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/orders';

  const REMEMBER_KEY = 'gf_remembered_email';

  const [loginFailCount, setLoginFailCount] = useState(0);
  const requireCaptcha = LOGIN_FAIL_CAPTCHA_THRESHOLD === 0 ? true : loginFailCount >= LOGIN_FAIL_CAPTCHA_THRESHOLD;
  const showCaptchaTriggeredNotice = LOGIN_FAIL_CAPTCHA_THRESHOLD > 0 && requireCaptcha;

  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({});
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirectPending, setRedirectPending] = useState(false);

  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaSession, setCaptchaSession] = useState<{
    imageId: string;
    answer: string;
    imageSrc: string;
  } | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaLoadError, setCaptchaLoadError] = useState<string | null>(null);

  const captchaReady =
    !!captchaSession?.imageId && !!captchaSession.imageSrc && !captchaLoadError && !captchaLoading;

  const loadCaptcha = useCallback(async (signal?: AbortSignal) => {
    setCaptchaLoadError(null);
    setCaptchaLoading(true);
    setCaptchaSession(null);
    setCaptchaValue('');
    setCaptchaValid(false);
    try {
      const d = await fetchLoginCaptcha({ signal });
      if (signal?.aborted) return;
      setCaptchaSession({
        imageId: d.image_id,
        answer: d.captcha_text,
        imageSrc: imageBase64ToDataUrl(d.image_base64),
      });
    } catch (err) {
      if (signal?.aborted || isCancel(err)) return;
      setCaptchaSession(null);
      setCaptchaLoadError(err instanceof Error ? err.message : 'Failed to load captcha.');
    } finally {
      if (!signal?.aborted) setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!requireCaptcha) {
      setCaptchaSession(null);
      setCaptchaLoadError(null);
      setCaptchaValue('');
      setCaptchaValid(false);
      return;
    }
    const ac = new AbortController();
    void loadCaptcha(ac.signal);
    return () => ac.abort();
  }, [requireCaptcha, loadCaptcha]);

  useEffect(() => {
    const st = location.state as { passwordResetOk?: boolean; from?: string } | null;
    if (!st?.passwordResetOk) return;
    setFormSuccess('Password updated successfully. Please sign in again with your new password.');
    navigate(location.pathname, { replace: true, state: st.from ? { from: st.from } : {} });
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setRedirectPending(false);
    const fe = computeFieldErrors(email, password, requireCaptcha, captchaReady, captchaValid);
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) {
      requestAnimationFrame(() => {
        if (fe.email) document.getElementById('login-email')?.focus();
        else if (fe.password) document.getElementById('login-password')?.focus();
        else if (fe.captcha) document.getElementById('login-captcha')?.focus();
      });
      return;
    }

    if (rememberMe) {
      localStorage.setItem(REMEMBER_KEY, email);
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    setLoading(true);
    const result = await login(
      email,
      password,
      requireCaptcha && captchaSession
        ? { captchaImageId: captchaSession.imageId, captchaCode: captchaValue.trim() }
        : undefined
    );
    setLoading(false);
    if (result.success) {
      setLoginFailCount(0);
      setCaptchaValue('');
      setCaptchaValid(false);
      setCaptchaSession(null);
      setFieldErrors({});
      const okMsg = result.message?.trim();
      if (okMsg) {
        setFormSuccess(okMsg);
        setRedirectPending(true);
        window.setTimeout(() => {
          navigate(from, { replace: true });
        }, 1200);
      } else {
        navigate(from, { replace: true });
      }
    } else {
      setLoginFailCount((c) => c + 1);
      setFieldErrors({});
      setFormError(result.error || 'Login failed.');
      if (requireCaptcha) void loadCaptcha();
    }
  };

  const emailInvalid = !!fieldErrors.email;
  const passwordInvalid = !!fieldErrors.password;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 relative">
      {/* Back to Home */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-1.5 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer group"
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 group-hover:border-gray-300 transition-colors">
          <i className="ri-arrow-left-line text-sm"></i>
        </div>
        <span className="text-xs font-medium">Back to Home</span>
      </button>

      {/* Logo */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-14 h-14 rounded-lg bg-green-600 flex items-center justify-center mb-4">
          <i className="ri-leaf-line text-white text-2xl"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">finfins</h1>
        <p className="text-sm text-gray-500 mt-1">Sign in to your workspace</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-lg border border-gray-100 p-3">
        <form lang="en" noValidate onSubmit={handleSubmit} className="space-y-3">
          {/* Email */}
          <div>
            <label htmlFor="login-email" className="block text-xs font-normal text-gray-600 mb-1.5">
              Email
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-mail-line text-base"></i>
              </div>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="you@example.com"
                aria-invalid={emailInvalid}
                aria-describedby={emailInvalid ? 'login-email-error' : undefined}
                className={`w-full pl-9 pr-4 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  emailInvalid
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
            </div>
            <InlineFieldError id="login-email-error" message={fieldErrors.email} />
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="login-password" className="block text-xs font-normal text-gray-600">
                Password
              </label>
              <button
                type="button"
                onClick={() => navigate('/login/password', { state: { email: email.trim(), from } })}
                className="text-xs font-semibold text-green-700 hover:underline cursor-pointer"
              >
                Change password
              </button>
            </div>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-lock-line text-base"></i>
              </div>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => {
                  setPassword(ev.target.value);
                  setFieldErrors((prev) => ({ ...prev, password: undefined }));
                }}
                placeholder="••••••••"
                aria-invalid={passwordInvalid}
                aria-describedby={passwordInvalid ? 'login-password-error' : undefined}
                className={`w-full pl-9 pr-10 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  passwordInvalid
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
              </button>
            </div>
            <InlineFieldError id="login-password-error" message={fieldErrors.password} />
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-4 h-4 rounded flex items-center justify-center border transition cursor-pointer flex-shrink-0 ${rememberMe ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300'}`}
            >
              {rememberMe && <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>}
            </button>
            <span
              className="text-xs text-gray-500 cursor-pointer select-none"
              onClick={() => setRememberMe(!rememberMe)}
            >
              Remember my email
            </span>
          </div>

          {requireCaptcha && (
            <div className="space-y-3">
              {showCaptchaTriggeredNotice && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 leading-relaxed">
                  Too many failed sign-in attempts. Please complete the verification code below to continue.
                </p>
              )}
              <CaptchaInput
                value={captchaValue}
                onChange={(v) => {
                  setCaptchaValue(v);
                  setFieldErrors((prev) => ({ ...prev, captcha: undefined }));
                }}
                onValidChange={setCaptchaValid}
                expectedAnswer={captchaSession?.answer ?? ''}
                imageSrc={captchaSession?.imageSrc ?? null}
                loading={captchaLoading}
                loadError={captchaLoadError}
                onRefresh={() => void loadCaptcha()}
                error={fieldErrors.captcha}
                inputId="login-captcha"
              />
              <InlineFieldError id="login-captcha-error" message={fieldErrors.captcha} />
            </div>
          )}

          {formSuccess ? (
            <FormSummaryMessage message={formSuccess} variant="info" />
          ) : (
            <FormSummaryMessage message={formError} />
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || redirectPending}
            className="w-full h-11 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-normal rounded-lg transition disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-base"></i>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Hint */}
        <p className="text-center text-xs text-gray-400 mt-6 leading-relaxed">
          Enter your work email and password<br />to access your team workspace.
        </p>

        <p className="text-center text-xs text-gray-500 mt-5">
          New here?{' '}
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="text-green-700 font-semibold hover:underline cursor-pointer"
          >
            Create an account
          </button>
        </p>
      </div>

      <p className="text-xs text-gray-400 mt-8 text-center">
        &copy; finfins 
      </p>
    </div>
  );
}
